import { ROUTE_HOME } from "@/constants/routes.constants";
import MainDropdown from "@/infrastructure/Navbar/mainDropdown.component";
import { NetworkStatusContext } from "@/services/NetworkContext/networkStatusContext";
import {
  BadgeCheck,
  ShieldAlert,
  AlignJustify as SidebarIcon,
  Wifi,
  WifiOff,
} from "lucide-react";
import React, { Dispatch, useContext, useState } from "react";
import { useNavigate } from "react-router";

import { AppContext } from "@/services/AppContext/appContext";
import { useTheme } from "@/services/ThemeContext/ThemeContext";
import { Skeleton } from "@/shadcn/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { useTranslation } from "react-i18next";

type NavbarProps = {
  currPage: string;
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<React.SetStateAction<boolean>>;
};

const Navbar: React.FC<NavbarProps> = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { logo } = useTheme();
  const { setSelectedAgentModel } = useContext(AppContext);

  const { isOnline, isApiOnline, isAuthenticated } =
    useContext(NetworkStatusContext);
  const [isLogoLoading, setIsLogoLoading] = useState(true);

  return (
    <div
      className={`bg-card z-20 grid w-full grid-cols-2 items-center gap-4 p-2 shadow-sm`}
    >
      {/* SideBar Icon and Logo / Name */}
      <div className="order-1 flex items-center justify-start sm:order-1">
        <SidebarIcon
          className="mr-3 h-5 w-5 cursor-pointer"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        />
        <div className="mr-2 font-medium md:block">
          <div
            className="flex cursor-pointer flex-row flex-nowrap select-none"
            onClick={() => {
              setSelectedAgentModel(null);
              nav(ROUTE_HOME);
            }}
          >
            {isLogoLoading && (
              <Skeleton className="bg-input h-8 w-8 rounded-full" />
            )}
            <img
              src={logo}
              alt="Logo"
              className={`h-8 ${isLogoLoading ? "hidden" : "block"}`}
              onLoad={() => setIsLogoLoading(false)}
              onError={() => setIsLogoLoading(false)}
            />
            {/* <div className="ml-3 text-xl font-bold">{displayName}</div> */}
          </div>
        </div>
      </div>
      {/* Search Bar */}
      {/* <div className="order-3 col-span-2 hidden items-center justify-center sm:order-2 sm:col-span-1 md:flex">
        <div className="hidden w-full sm:max-w-md md:block md:max-w-xs">
          <Popover>
            <PopoverTrigger className="w-full">
              <Input
                type="search"
                className="bg-background w-full"
                placeholder={t("Search")}
              />
            </PopoverTrigger>
            <PopoverContent className="" side="bottom" align="center">
              {t("Unavailable: Demo Mode")}
            </PopoverContent>
          </Popover>
        </div>
      </div> */}
      {/* Alerts and Profile Icons */}
      <div className="order-2 flex items-center justify-end gap-3 sm:order-3">
        <div className="flex gap-2 rounded-md bg-neutral-500/20 p-2">
          {isOnline ? (
            <TooltipProvider>
              <Tooltip delayDuration={150}>
                <TooltipTrigger>
                  <Wifi
                    className={`h-4 w-4 cursor-default ${
                      isApiOnline ? "!text-green-600" : "!text-orange-500"
                    } `}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isApiOnline
                      ? t("Connection is Online")
                      : t("Connection is Offline")}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger>
                  <WifiOff className="h-4 w-4 cursor-default !text-red-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Internet is Offline")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isAuthenticated ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <BadgeCheck className="h-4 w-4 cursor-default !text-green-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Authenticated")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <ShieldAlert className="h-4 w-4 cursor-default !text-red-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Not Authenticated")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {/* <NavbarNotifications /> */}
        <div className="">
          <MainDropdown />
        </div>
      </div>
    </div>
  );
};

export default Navbar;
